import { LegacyReportSummaryItem } from "../api/LegacyReportsApi";
import CollapsibleSection from "./common/CollapsibleSection";
import LegacyDonutChart from "./common/LegacyDonutChart";

import { SocialIcon } from "../utils/social-icon";

interface PersonalInfoProps {
  info: Pick<
    LegacyReportSummaryItem,
    | "name"
    | "emailList"
    | "locationList"
    | "jobList"
    | "degreeList"
    | "phoneList"
    | "screeningCompanyName"
  >;
}

interface SocialProfilesSectionProps {
  metrics: LegacyReportSummaryItem["webPersonMetrics"];
}

function PersonalInfoSection({ info }: PersonalInfoProps) {
  if (!info) return null;

  return (
    <div className="bg-white p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{info.name}</h2>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-xs text-gray-600">Screening Company</h3>
          <div className="ml-auto">{info.screeningCompanyName}</div>
        </div>

        <CollapsibleSection title="Employment">
          <ul className="text-sm space-y-2">
            {info.jobList?.map((job, i) => (
              <li key={i}>
                {job.name ? `${job.name} - ` : ""}
                {job.employer}
              </li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection title="Education">
          <ul className="text-sm space-y-2">
            {info.degreeList?.map((edu, i) => (
              <li key={i}>
                {edu.name ? `${edu.name} - ` : ""}
                {edu.educator}
              </li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection title="Addresses">
          <ul className="text-sm space-y-2">
            {info.locationList?.map((addr, i) => (
              <li key={i}>{addr.formattedAddress}</li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection title="Phone">
          <ul className="text-sm space-y-2">
            {info.phoneList?.map((phone, i) => (
              <li key={i}>{phone.number}</li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection title="Email">
          <ul className="text-sm space-y-2">
            {info.emailList?.map((email, i) => (
              <li key={i}>{email.email}</li>
            ))}
          </ul>
        </CollapsibleSection>
      </div>
    </div>
  );
}

function SocialProfileLabel({
  platform,
  label,
  url,
}: {
  platform: string;
  label: string;
  url: string;
}) {
  return (
    <div className="flex items-center">
      <SocialIcon network={platform} className="!w-6 !h-6 mr-2" url={url} />
      <span className="text-s text-gray-600">{label}</span>
    </div>
  );
}

function ProfileSummarySection({ metrics }: SocialProfilesSectionProps) {
  if (!metrics?.profileUrlsByProfileType) return null;

  const socialPlatforms = {
    TWITTER: <SocialProfileLabel platform="x" label="X" url="https://x.com" />,
    FACEBOOK: (
      <SocialProfileLabel
        platform="facebook"
        label="Facebook"
        url="https://facebook.com"
      />
    ),
    INSTAGRAM: (
      <SocialProfileLabel
        platform="instagram"
        label="Instagram"
        url="https://instagram.com"
      />
    ),
    LINKEDIN: (
      <SocialProfileLabel
        platform="linkedin"
        label="LinkedIn"
        url="https://linkedin.com"
      />
    ),
  };

  const otherProfiles = Object.entries(metrics.profileUrlsByProfileType).filter(
    ([platform]) => !socialPlatforms[platform as keyof typeof socialPlatforms]
  );

  return (
    <div className="flex flex-col pl-4">
      <h2 className="text-base font-semibold text-gray-800 mt-4 mb-4">
        Social Profiles
      </h2>
      {Object.entries(socialPlatforms).map(([platform, label]) => {
        const urls = metrics.profileUrlsByProfileType[platform];
        if (!urls || urls.length === 0) return null;

        return (
          <div className="mb-4" key={platform}>
            <CollapsibleSection title={label}>
              <ul className="text-sm mb-2 space-y-2">
                {urls.map((url: string) => {
                  return (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {url}
                    </a>
                  );
                })}
              </ul>
            </CollapsibleSection>
          </div>
        );
      })}

      {otherProfiles.length > 0 && (
        <div className="mt-6">
          <CollapsibleSection title="Other">
            <ul className="text-sm space-y-2">
              {otherProfiles.map(([platform, urls]) =>
                urls.map((url: string) => {
                  return (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {url}
                    </a>
                  );
                })
              )}
            </ul>
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}

export default function LegacyReportSummaryHeader({
  report,
}: {
  report: LegacyReportSummaryItem;
}) {
  return (
    <div>
      <div className="w-full bg-white relative" id="stick-container">
        <div className="container mx-auto">
          <div className="flex pt-8">
            <div className="w-1/3 border-r border-gray-200 pb-8 pr-8">
              <PersonalInfoSection info={report} />
            </div>
            <div className="w-[38%] border-r border-gray-200 pb-8">
              <LegacyDonutChart metrics={report.webPersonMetrics} />
            </div>
            <div className="flex-1 pl-2 pr-8">
              <ProfileSummarySection metrics={report.webPersonMetrics} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
